/**
 * Loader'ları prototype seviyesinde sarmalayıp YÜKLENEN her asset'i otomatik
 * AssetRegistry'ye kaydeder. Kritik nokta: bir asset sahneye eklenmese bile
 * (örn. yüklenip beklemeye alınan bir model) burada yakalanır — çünkü yükleme
 * anına müdahale ediyoruz, sahneye değil.
 *
 * SceneMutationObserver'ın yaptığı gibi tek seferlik bir monkey-patch; bir kez
 * kurulduktan sonra hangi kod bu loader'ları kullanırsa kullansın
 * (geliştiricinin haberi olmadan) asset'ler izlenir.
 *
 * İki sarmalama deseni var:
 *   - patchReturn   : metot sonucu SENKRON döner (parse → Group, load → Texture).
 *   - patchCallback : sonuç onLoad callback'i ile gelir (GLTF, KTX2 gibi async).
 * Geometry/group döndürenlerde track(graph), texture döndürenlerde trackTexture.
 */
import { TextureLoader } from "three";
import { FBXLoader } from "three/examples/jsm/loaders/FBXLoader.js";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import { KTX2Loader } from "three/examples/jsm/loaders/KTX2Loader.js";
import { OBJLoader } from "three/examples/jsm/loaders/OBJLoader.js";
import { HDRLoader } from "three/examples/jsm/loaders/HDRLoader.js";
import { AssetRegistry } from "./AssetRegistry";

type AnyProto = Record<string, unknown>;
type AnyFn = (...args: unknown[]) => unknown;

let installed = false;

export function installAssetTracking(): void {
  if (installed) return;
  installed = true;

  const proto = (o: object): AnyProto => o as unknown as AnyProto;

  // --- senkron sonuç döndüren loader'lar ---
  // Geometry/group: parse(...) → Object3D graph. load() de nihayetinde parse'a
  // indiği için tek nokta yeterli.
  patchReturn(proto(FBXLoader.prototype), "parse", (g) =>
    AssetRegistry.track(g as never)
  );
  patchReturn(proto(OBJLoader.prototype), "parse", (g) =>
    AssetRegistry.track(g as never)
  );
  // Texture: load(...) → Texture.
  patchReturn(proto(TextureLoader.prototype), "load", (t) =>
    AssetRegistry.trackTexture(t as never)
  );
  patchReturn(proto(HDRLoader.prototype), "load", (t) =>
    AssetRegistry.trackTexture(t as never)
  );

  // --- sonucu onLoad callback'i ile veren loader'lar ---
  // GLTFLoader.parse(data, path, onLoad, …) → onLoad(gltf); gltf.scene(ler)i track.
  patchCallback(proto(GLTFLoader.prototype), "parse", 2, (gltf) => {
    const g = gltf as { scene?: unknown; scenes?: unknown[] };
    const scenes = g.scenes?.length ? g.scenes : g.scene ? [g.scene] : [];
    scenes.forEach((s) => AssetRegistry.track(s as never));
  });
  // KTX2Loader.load(url, onLoad, …) → onLoad(texture).
  patchCallback(proto(KTX2Loader.prototype), "load", 1, (tex) =>
    AssetRegistry.trackTexture(tex as never)
  );
}

/** Metodu sarmala; SENKRON dönen sonucu handle'a ver (track asla yüklemeyi bozmamalı). */
function patchReturn(
  proto: AnyProto,
  method: string,
  handle: (result: unknown) => void
): void {
  const orig = proto[method] as AnyFn | undefined;
  if (typeof orig !== "function") return;
  proto[method] = function (this: unknown, ...args: unknown[]): unknown {
    const result = orig.apply(this, args);
    try {
      handle(result);
    } catch {
      /* yut */
    }
    return result;
  };
}

/** Metodu sarmala; cbIndex'teki onLoad callback'ine gelen sonucu handle'a ver. */
function patchCallback(
  proto: AnyProto,
  method: string,
  cbIndex: number,
  handle: (result: unknown) => void
): void {
  const orig = proto[method] as AnyFn | undefined;
  if (typeof orig !== "function") return;
  proto[method] = function (this: unknown, ...args: unknown[]): unknown {
    const cb = args[cbIndex] as ((result: unknown) => void) | undefined;
    if (typeof cb === "function") {
      args[cbIndex] = (result: unknown) => {
        try {
          handle(result);
        } catch {
          /* yut */
        }
        cb(result);
      };
    }
    return orig.apply(this, args);
  };
}
